
import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
	destroy,
	Field,
	getFormValues,
	isValid,
	reduxForm
} from "redux-form";
import {
	Blade,
	Button,
	ReduxControls,
	ProgressIndicator
} from "rhombus-react";

import { push } from 'connected-react-router';
import { CREATE_NATIVE_AD_FORM } from "forms/list";
import { NATIVE_MAX, nativeNameMaxLength } from 'forms/validators/Creatives';
import { required, validNativeName } from "forms/validators/validators";

import { createNative } from "reducers/CreativeDetails";
import { clearVersionDetails, selectVersionType } from "reducers/VersionDetails";
import { filteredEditVersionValues } from "selectors/VersionDetails";

import { getAssetOptionsFromPayload } from "../versions/EditVersionBlade";
import TextEditor from "../versions/TextEditor";
import AssetEditor from "../versions/AssetEditor";

import useSearchCache from "hooks/useSearchCache";
import { getVariables } from "utils/VersionHelpers";

import "./CreateNativeAdModal.css";

const editorsByType = {
	Text: TextEditor,
	Image: AssetEditor,
	Video: AssetEditor,
	Richload: AssetEditor,
};

// Props for fields that use the editor
export const defaultValues = {
	feedEndpoint: {
		name: "feedEndpoint",
		data: "",
		prop: "",
		type: "Text",
		fieldProps: {
			required: true,
		},
	},
	headline: {
		name: "headline",
		data: "",
		prop: "0",
		type: "Text",
		isFormatted: false,
		fieldProps: {
			required: true,
		},
	},
	defaultHeadline: { //feed type
		name: "defaultHeadline",
		data: "",
		prop: "",
		type: "Text",
		fieldProps: {
			required: true,
		},
	},
	body: {
		name: "body",
		data: "",
		prop: "0",
		type: "Text",
		isFormatted: false,
	},
	defaultBody: { //feed type
		name: "defaultBody",
		data: "",
		prop: "",
		type: "Text",
		isFormatted: false,
	},
	image1: {
		name: "image1",
		data: "",
		prop: "0",
		type: "Image",
		fieldProps: {
			required: true,
		},
	},
	renderedImage: { //feed type
		name: "renderedImage",
		placeholder: "Enter feed image URL...",
		data: "",
		prop: "",
		type: "Text",
		fieldProps: {
			required: true,
		},
	},
	defaultImage: { //feed type
		name: "defaultImage",
		data: "",
		prop: "",
		type: "Image",
		fieldProps: {
			required: true,
		},
	},
	clickthrough1: {
		name: "clickthrough1",
		data: "",
		prop: "0",
		isFormatted: false,
		type: "Text",
	},
	defaultClickthrough: { //feed type
		name: "defaultClickthrough",
		data: "",
		prop: "",
		isFormatted: false,
		type: "Text",
		fieldProps: {
			required: true,
		},
	},
	brand: {
		name: "brand",
		data: "",
		prop: "0",
		type: "Text",
		isFormatted: false,
		fieldProps: {
			required: true,
		},
	},
	logo: {
		name: "logo",
		data: "",
		prop: "0",
		type: "Image",
		fieldProps: {
			required: true,
		},
	},
	imageFrame: { //feed type
		name: "imageFrame",
		data: "",
		prop: "",
		type: "Image",
		fieldProps: {
			required: true,
		},
	},
	segmentId: { //feed type
		name: "segmentId",
		label: "segmentid",
		data: "",
		prop: "",
		type: "Text",
	},
	reportingId: { //feed type
		name: "reportingId",
		label:"reportingId",
		data: "",
		prop: "",
		type: "Text",
	},
	dynamicToggle: "dynamic",
};

const DYNAMIC_TYPE = [
	defaultValues.headline.name,
	defaultValues.body.name,
	defaultValues.image1.name,
	defaultValues.clickthrough1.name,
	defaultValues.brand.name,
	defaultValues.logo.name,
];

const FEED_TYPE = [
	defaultValues.feedEndpoint.name,
	defaultValues.headline.name,
	defaultValues.defaultHeadline.name,
	defaultValues.body.name,
	defaultValues.defaultBody.name,
	defaultValues.renderedImage.name,
	defaultValues.defaultImage.name,
	defaultValues.clickthrough1.name,
	defaultValues.defaultClickthrough.name,
	defaultValues.brand.name,
	defaultValues.logo.name,
	defaultValues.imageFrame.name,
	defaultValues.segmentId.name,
	defaultValues.reportingId.name,
];

function _preProcess(values){
	let newFormValues = { creativeName: values.creativeName }
		, type = values.dynamicToggle
		, currentValue = null
		;

	for(currentValue in values){
		// eslint-disable-next-line no-unused-vars
		let { fieldProps, ...allProps } = values[currentValue];

		if(type === "dynamic"){
			if(DYNAMIC_TYPE.includes(currentValue)){
				newFormValues[currentValue] = { ...allProps };
			}
		} else {
			if(FEED_TYPE.includes(currentValue)){
				newFormValues[currentValue] = { ...allProps };
			}
		}
	}

	return newFormValues;
}


let CreateNativeForm = (props) => {
	const dispatch = useDispatch()
		, {
			children,
			handleSubmit,
			libraryId,
			onSave,
		} = props
		// TODO: We shouldn't need this, the `handleSubmit(onSubmit)` should send through the correct values, but it's stripping things
		, currentValues = useSelector(state => getFormValues(CREATE_NATIVE_AD_FORM)(state))
		;

	function onSubmit(values){
		// let newFormValues = _preProcess(values);
		let newFormValues = _preProcess(currentValues);

		return dispatch(createNative(
			libraryId,
			newFormValues.creativeName,
			getVariables(newFormValues, libraryId)
		)).then((resp) => {
			if(resp?.success) {
				return onSave();
			}
		})
		;
	}

	return (
		<form className="form" id={CREATE_NATIVE_AD_FORM} onSubmit={handleSubmit(onSubmit)}>
			<div className="form-header">
				<Field
					name="creativeName"
					label="Creative Name"
					required
					validate={[required, nativeNameMaxLength, validNativeName]}
					showCharCount
					charMax={NATIVE_MAX}
					component={ReduxControls.Input}
				/>
				<div className="radio-controls">
					<Field
						component={ReduxControls.Checkbox}
						name="dynamicToggle"
						label="Dynamic"
						type="radio"
						value="dynamic"
					/>
					<Field
						component={ReduxControls.Checkbox}
						name="dynamicToggle"
						label="Feed"
						type="radio"
						value="feed"
					/>
				</div>
			</div>
			<table>
				<tbody>
					{children}
				</tbody>
			</table>
		</form>
	);
};

CreateNativeForm = reduxForm({
	form: CREATE_NATIVE_AD_FORM,
	initialValues: defaultValues,
})(CreateNativeForm);

export default function CreateNativeAd(props) {
	let dispatch = useDispatch()
		, { returnPath, libraryId } = props
		;
	const isUpdating = useSelector(state => state.creatives?.isUpdating);
	const currentFormValues = useSelector(state => filteredEditVersionValues(state, CREATE_NATIVE_AD_FORM));
	const formValues = useSelector(getFormValues(CREATE_NATIVE_AD_FORM));
	const isSaving = useSelector(state => state.creativeDetails?.isSaving);
	const isFormValid = useSelector(isValid(CREATE_NATIVE_AD_FORM));

	const { search: searchAssets } = useSearchCache({
		key: "assets",
		urlParams: { libraryId },
		transform: getAssetOptionsFromPayload,
	});

	useEffect(() => {
		// Initialize Form Field Values and Version Types
		dispatch(selectVersionType("Text"));
		dispatch(selectVersionType("Image"));
	}, []);

	function closeModal(){
		dispatch(push(returnPath));
		dispatch(destroy(CREATE_NATIVE_AD_FORM));
		dispatch(clearVersionDetails());
	}

	let filteredValues = useMemo(() => {
		return Object.values(currentFormValues).filter((value) => {
			return ((formValues.dynamicToggle === "dynamic" && DYNAMIC_TYPE.includes(value.name)) || (formValues.dynamicToggle === "feed" && FEED_TYPE.includes(value.name)));
		});
	}, [formValues?.dynamicToggle, currentFormValues]);

	const VariableRows = useMemo(() => {
		return filteredValues.map((value) => {
			let { name, type, placeholder } = value;

			let Editor = editorsByType[type];
			return (
				<Editor
					key = {name}
					value = {value}
					form = {CREATE_NATIVE_AD_FORM}
					libraryId = {libraryId}
					placeholder = {placeholder}
					search = {searchAssets}
				/>
			);
		});
	}, [filteredValues]);

	const renderContent = () => (
		<div className="create-native-ad-form" >
			<ProgressIndicator isLoading={isUpdating || isSaving} />
			<CreateNativeForm onSave={closeModal} libraryId={libraryId}>
				{VariableRows}
			</CreateNativeForm>
		</div>
	);

	const renderFooter = () => ([
		<Button key="cancel" theme="grey" disabled={isUpdating || isSaving} onClick={closeModal}>Cancel</Button>,
		<Button key="submit" type="submit" theme="green" disabled={isUpdating || isSaving || !isFormValid} form={CREATE_NATIVE_AD_FORM}>
			Create Native Ad
		</Button>
	]);

	return (
		<Blade
			type="modal"
			setAppTitle={false}
			className="creative-action-modal create-native-ad"
			title="Create Native Ad"
			allowFullScreen={false}
			xClose
			bladeProps={{ verticallyCenter: true }}
			onClose={closeModal}
			renderContent={renderContent}
			renderFooter={renderFooter}
		/>
	);
}

CreateNativeAd.displayName = "CreateNativeAd";
